"use client";

import { ComponentProps } from "react";
import { useIsShopsite } from "@context/DomainContext";
import { Table as RadixTable } from "@radix-ui/themes";
import { joinClassNames } from "@utils/helpers";

interface StyleProps {
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  sharedClasses?: string;
  shopsitePadding?: "normal" | "tight";
}

export const Root = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  ...props
}: ComponentProps<typeof RadixTable.Root> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = "border rounded-lg border-gray-200";

  if (isShopsite) {
    return (
      <div
        {...props}
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        <table className="border-collapse w-full">{children}</table>
      </div>
    );
  }

  return (
    <RadixTable.Root
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.Root>
  );
};

export const Body = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  ...props
}: ComponentProps<typeof RadixTable.Body> & StyleProps) => {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return (
      <tbody
        {...props}
        className={joinClassNames(className, sharedClasses, shopsiteClasses)}
      >
        {children}
      </tbody>
    );
  }

  return (
    <RadixTable.Body
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.Body>
  );
};

export const Row = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  ...props
}: ComponentProps<typeof RadixTable.Row> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = "[&:not(:last-child)]:border-b border-gray-200";

  if (isShopsite) {
    return (
      <tr
        {...props}
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </tr>
    );
  }

  return (
    <RadixTable.Row
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.Row>
  );
};

export const Cell = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  shopsitePadding = "normal",
  ...props
}: Omit<ComponentProps<typeof RadixTable.Cell>, "width"> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = joinClassNames(
    "bg-transparent text-black first:pl-6 last:pr-6 md:last:text-right",
    shopsitePadding === "tight" ? "py-4 px-2" : "py-5 px-3"
  );

  if (isShopsite) {
    return (
      <td
        {...props}
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </td>
    );
  }

  return (
    <RadixTable.Cell
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.Cell>
  );
};

export const Header = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  ...props
}: ComponentProps<typeof RadixTable.Header> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = "border-b border-gray-200";

  if (isShopsite) {
    return (
      <thead
        {...props}
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </thead>
    );
  }

  return (
    <RadixTable.Header
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.Header>
  );
};

export const ColumnHeaderCell = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  shopsitePadding = "normal",
  ...props
}: ComponentProps<typeof RadixTable.ColumnHeaderCell> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = joinClassNames(
    "bg-transparent text-black font-semibold first:pl-6 last:pr-6 md:last:text-right",
    shopsitePadding === "tight" ? "py-4 px-2" : "py-5 px-3"
  );

  if (isShopsite) {
    return (
      <th
        {...props}
        scope="col"
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </th>
    );
  }

  return (
    <RadixTable.ColumnHeaderCell
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.ColumnHeaderCell>
  );
};

export const RowHeaderCell = ({
  children,
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  shopsitePadding = "normal",
  ...props
}: ComponentProps<typeof RadixTable.RowHeaderCell> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = joinClassNames(
    "bg-transparent text-black font-semibold first:pl-6 last:pr-6",
    shopsitePadding === "tight" ? "py-4 px-2" : "py-5 px-3"
  );

  if (isShopsite) {
    return (
      <th
        {...props}
        scope="row"
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </th>
    );
  }

  return (
    <RadixTable.RowHeaderCell
      {...props}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixTable.RowHeaderCell>
  );
};
